// Projecting a surface pose onto a PARALLEL surface — "the same place on the
// facing wall", "straight down from that ceiling panel".
//
// This exists as its own module, with its own tests, because the obvious
// implementation is wrong in a way that a reasonable test suite will not
// catch.
//
// A wall frame's in-plane axis is built as `u = UP × n` (see
// panelSurface.ts's `surfacesForPolygon`). Two FACING walls have opposite
// inward normals, so their `u` axes run in opposite world directions. A panel
// at `u = 3` on one wall is therefore NOT at `u = 3` on the wall opposite —
// it is at whatever `u` puts it at the same world point, which on a plain
// rectangle works out to `uMax − 3`. Ceiling and floor are worse: their `u`
// axes agree (+X from the same minX) while their `v` axes are +Z from minZ
// and −Z from maxZ, so half the coordinates copy across correctly and half
// silently flip.
//
// Both of those make `u_to = u_from` produce right answers often enough to
// look correct: on a square room with a centred object it is exactly right,
// and on any rectangle `uMax − u` is right too. Neither survives a room whose
// facing wall is a different length or offset along the axis — an L-shaped
// room's two parallel runs — where a naive formula still returns a plausible
// in-range number that points somewhere else entirely.
//
// So there is exactly one correct route, and it is the one below: go out to
// world space and come back. No axis bookkeeping, no per-surface-kind special
// cases, and it handles walls that are not axis-aligned for free.

import * as THREE from 'three';
import type { SurfaceFrame } from './panelSurface';
import { sameSurface } from './panelSurface';

/** How close two normals must be to (anti)parallel before the surfaces count
 *  as mirrors of each other. 1e-3 on a dot product is about 2.5° — tight
 *  enough that a chamfer is never mistaken for a facing wall, loose enough to
 *  survive the float error in a polygon built from user-entered dimensions. */
const PARALLEL_EPSILON = 1e-3;

/** Are these two surfaces parallel — facing each other, or facing the same way
 *  from different depths? Both count: a facing wall is the obvious mirror, and
 *  two same-facing runs of an L-shaped room's wall are a real alignment
 *  relationship too ("line it up with the panel on the other run"). */
export function surfacesAreParallel(
  a: SurfaceFrame, b: SurfaceFrame, epsilon: number = PARALLEL_EPSILON,
): boolean {
  return Math.abs(Math.abs(a.n.dot(b.n)) - 1) <= epsilon;
}

/**
 * Where the point at `(u, v)` on `from` lands in `to`'s own (u, v).
 *
 * Total, and deliberately does no range checking — a mirror that falls off the
 * end of a shorter facing wall is a real and common outcome (see
 * `poseWithinSurface`), not an error, and the caller has the panel's
 * half-extents that the range test needs.
 *
 * Only meaningful for surfaces that are actually parallel; on skew surfaces it
 * returns the projection of an off-plane point, which is arithmetic without a
 * meaning. `mirrorSurfaces` is the gate.
 */
export function projectPoseOntoSurface(
  from: SurfaceFrame, to: SurfaceFrame, u: number, v: number,
): { u: number; v: number } {
  const world = from.origin.clone().addScaledVector(from.u, u).addScaledVector(from.v, v);
  const rel = world.sub(to.origin);
  return { u: rel.dot(to.u), v: rel.dot(to.v) };
}

/** Does a panel of half-extents `(hu, hv)` centred at `(u, v)` fit inside
 *  `frame`? The range test `projectPoseOntoSurface` deliberately omits. */
export function poseWithinSurface(
  frame: SurfaceFrame, u: number, v: number, hu: number, hv: number,
): boolean {
  return u >= hu - 1e-9 && u <= frame.uMax - hu + 1e-9
    && v >= hv - 1e-9 && v <= frame.vMax - hv + 1e-9;
}

/** The surfaces `frame` can mirror onto: every other mountable surface
 *  parallel to it. Corner frames are excluded on both sides — a corner trap
 *  has no in-plane extent to mirror, and its `n` is a bisector that can sit
 *  parallel to a wall by coincidence without meaning anything. */
export function mirrorSurfaces(surfaces: SurfaceFrame[], frame: SurfaceFrame): SurfaceFrame[] {
  if (frame.ref.kind === 'corner') return [];
  return surfaces.filter((s) => (
    s.ref.kind !== 'corner'
    && !sameSurface(s.ref, frame.ref)
    && surfacesAreParallel(s, frame)
  ));
}

/**
 * The surfaces whose objects share a HEIGHT axis with `frame`: every other
 * wall, parallel or not.
 *
 * Separate from `mirrorSurfaces` because it is a weaker and much more widely
 * applicable relationship. Every wall frame is anchored at floor level with
 * `v = UP` (`surfacesForPolygon` builds them from a polygon point at y = 0),
 * so `v` on any wall is literally height above the floor and a panel's height
 * is comparable against every other wall in the room regardless of which
 * direction that wall faces. Lining panels up at a consistent height right
 * around a room is a thing people do constantly, and it needs no parallelism
 * at all.
 *
 * Empty for a ceiling or floor frame, where `v` is a plan axis and "height"
 * is not a thing that surface has.
 */
export function heightPeerSurfaces(surfaces: SurfaceFrame[], frame: SurfaceFrame): SurfaceFrame[] {
  if (frame.ref.kind !== 'wall') return [];
  return surfaces.filter((s) => s.ref.kind === 'wall' && !sameSurface(s.ref, frame.ref));
}

/** World point of a pose on a surface, ignoring panel depth — the shared
 *  primitive behind the projection above, exported because the guide overlay
 *  needs to draw a mirror line on the SOURCE surface too. */
export function poseToWorldPoint(frame: SurfaceFrame, u: number, v: number): THREE.Vector3 {
  return frame.origin.clone().addScaledVector(frame.u, u).addScaledVector(frame.v, v);
}
